import { useEffect, useState } from "react";
import { Row } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import LoadingPlaceholder from "../components/loadingPlaceholder";
import FetchError from "../components/fetchError";
import PaymentStatus from "../components/paymentStatus";
import ProductsSummary from "../components/productSummary";
import { Badge, ProductItem } from "../components/styles";
import { client } from "../utils/api";
import { formatPrice, formatTime } from "../utils/format";
import { DELIVERY_ADDRESS } from "../modules/delivery/reducer";
import {
  forgetOrderThankyou,
  hasRememberedOrderThankyou,
} from "../utils/orderThankyou";

function Order() {
  const { hashid } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState({ products: [], date: "", status: "new" });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [loadedHash, setLoadedHash] = useState(null);
  const [thankyou] = useState(
    () =>
      Boolean(location?.state?.thankyou) || hasRememberedOrderThankyou(hashid)
  );

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    async function data() {
      setLoading(true);
      setNotFound(false);
      setError(null);
      setOrder({ products: [], date: "", status: "new" });
      try {
        const response = await client(`orders/orders/${hashid}/`, { signal });
        if (!response) throw new Error("Empty order response");
        if (signal.aborted) return;
        setOrder(response);
        forgetOrderThankyou(hashid);
      } catch (failure) {
        if (signal.aborted) return;
        if (failure?.response?.status === 404) setNotFound(true);
        else setError(failure);
      } finally {
        if (!signal.aborted) {
          setLoadedHash(hashid);
          setLoading(false);
        }
      }
    }

    data();
    return () => controller.abort();
  }, [hashid, attempt]);

  useEffect(() => {
    if (
      loadedHash !== hashid ||
      loading ||
      error ||
      order.payment_type !== "blik" ||
      order.is_paid ||
      !order.payment_pending
    )
      return;
    const controller = new AbortController();
    let busy = false;
    const timer = setInterval(async () => {
      if (busy) return;
      busy = true;
      try {
        await client(`orders/blik/status?order=${encodeURIComponent(hashid)}`, {
          signal: controller.signal,
        });
        const response = await client(`orders/orders/${hashid}/`, {
          signal: controller.signal,
        });
        if (!controller.signal.aborted && response) setOrder(response);
      } catch (_) {
      } finally {
        busy = false;
      }
    }, 3000);
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [
    hashid,
    loadedHash,
    loading,
    error,
    order.payment_type,
    order.is_paid,
    order.payment_pending,
  ]);

  if (loading || loadedHash !== hashid) {
    return <LoadingPlaceholder>Ładowanie zamówienia</LoadingPlaceholder>;
  }

  if (error) {
    return (
      <FetchError
        message="Nie udało się wczytać zamówienia. Sprawdź połączenie z internetem."
        onRetry={() => setAttempt((value) => value + 1)}
      />
    );
  }

  if (notFound) {
    return (
      <Row className="g-0 bg-white rounded border justify-content-center">
        <div className="my-5 text-center">
          <div style={{ fontSize: "55px" }} className="mb-2">
            😞
          </div>
          <h5>Nie odnaleziono</h5>
          <div className="mb-3">
            Zamówienie o takim identifykatorze nie zostało odnalezione
          </div>
          <Link to="/" replace>
            Powrót na stronę główną
          </Link>
        </div>
      </Row>
    );
  }

  return (
    <div className={`order-page${thankyou ? " order-page--thankyou" : ""}`}>
      {thankyou && (order.payment_type !== "blik" || order.is_paid) && (
        <section
          className="order-success"
          aria-labelledby="order-success-title"
        >
          <div className="order-success__copy">
            <span className="order-success__eyebrow">
              Przyjęliśmy zamówienie
            </span>
            <h2 id="order-success-title">
              Gotowe.
              <br />
              Teraz my.
            </h2>
          </div>
          <div className="order-success__details">
            <p>
              Zamówienie jest już po naszej stronie. Potwierdzenie wysłaliśmy na
              podany adres e-mail.
            </p>
            <div className="order-success__reference">
              <span>Numer zamówienia</span>
              <strong>{order.number || order.id}</strong>
            </div>
            <div className="order-success__stamp" aria-hidden="true">
              <span className="order-success__check" />
              <strong>Przyjęte</strong>
              <small>do realizacji</small>
            </div>
          </div>
        </section>
      )}
      <section className="order-receipt" aria-labelledby="order-details-title">
        <div className="order-receipt__header">
          <div>
            <span className="interface-label">Podsumowanie</span>
            <h3 id="order-details-title">Zamówienie nr {order.number || order.id}</h3>
          </div>
          <div>
            {order.is_canceled && <Badge color="#D63230">Anulowane</Badge>}
          </div>
        </div>
        <div className="order-receipt__body">
          <div className="order-facts">
            <div className="order-fact">
              <span>Na dzień</span>
              <strong>{order.date}</strong>
            </div>
            {order.delivery_type === DELIVERY_ADDRESS && (
              <div className="order-fact">
                <span>Dostawa na adres</span>
                <strong>
                  {order.delivery_address}
                  {order.delivery_address_extra && (
                    <small>{order.delivery_address_extra}</small>
                  )}
                </strong>
              </div>
            )}
            {order.delivery_type === "pickup" && (
              <div className="order-fact">
                <span>Odbiór</span>
                <strong>
                  {order.pickup_location.name} o {formatTime(order.pickup_at)}
                </strong>
              </div>
            )}
            <div className="order-fact">
              <span>Płatność</span>
              <strong>
                <PaymentStatus order={order} />
              </strong>
            </div>
            {order.comment && (
              <div className="order-fact order-fact--comment">
                <span>Komentarz</span>
                <strong>{order.comment}</strong>
              </div>
            )}
          </div>
          <div className="order-products">
            <ProductsSummary products={order.products} total={order.total}>
              {order.delivery_cost !== null && (
                <ProductItem>
                  Dostawa <div>{formatPrice(order.delivery_cost)} zł</div>
                </ProductItem>
              )}
            </ProductsSummary>
          </div>
        </div>
        <div className="order-receipt__footer">
          <span>Eatally · zamów online</span>
          <Link to="/">Wróć do Menu →</Link>
        </div>
      </section>
    </div>
  );
}

export default Order;
